import {
  closeSM,
  editPencil,
  favouriteStar,
  saveIcon,
  squarePlusSM,
} from "@/components/icons/SVGIcons";
import { Button } from "@/components/UI/Button";
import { generateToastMessageSelectingPanel } from "@/lib/notifications/toastNotificationHelpers";
import savedPanelsStore, { IPanelCounterparty, IPanelSelection } from "@/store/savedPanelsStore";
import {
  createNewPanel,
  defaultUserPanel,
  deletPanelItem,
  initialPanelsFromAPI,
  setPanelItemAsActive,
  setPanelItemAsfavourite,
  updatePanelCounterparties,
} from "@/store/savedPanelsStoreACTIONS";
import useTableUIStore from "@/store/tableUIStore";
import tradeDataStore from "@/store/tradeDataStore";
import tradeSettingsStore from "@/store/tradeSettingsStore";
import { setTradeSettingsSelectedCounterparties } from "@/store/tradeSettingsStoreACTIONS";
import { useAuth0 } from "@auth0/auth0-react";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useTheme } from "next-themes";
import { SetStateAction, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Tooltip } from "react-tooltip";
import { cubeCustomColors } from "../../../../../../tailwind.config";
import EditableTextRenameCP from "./EditableTextRenameCP";
import { findThisTradeSettings } from "./SelectPanelHelper";
import { TradeType } from "@/lib/interfaces and Enums/ENUMTradeType";
import { getCellElementHeight, getCellFontSizeRem } from "../../helper/actionSummaryHelper";

export interface IReactSelectSavePanel {
  // counterpartiesList: IToggleCounterpartiesButton[] | [];
  // setCounterpartiesList: Function;
  // currentStoreTrade: any; // change to store trade props
  currentActiveTab: IPanelSelection;
  setCurrentActiveTab: React.Dispatch<SetStateAction<IPanelSelection>>;
  data: any;
  theme: any;
  cryptoID: string | undefined;
  isEditPanel?: boolean;
}

const ReactSelectSavedPanel = (props: IReactSelectSavePanel) => {
  const {
    // counterpartiesList,
    // setCounterpartiesList,
    // setCounterpartiesListToSendBackend,
    currentActiveTab,
    setCurrentActiveTab,
    // currentStoreTrade,
    isEditPanel,
  } = props;
  const { getAccessTokenSilently } = useAuth0();
  const panelsData = savedPanelsStore((state) => state.userPanels);
  const data = props.data;

  const { theme } = useTheme();
  const rowHeight = useTableUIStore((state) => state.rowHeight);
  const fontSizeSetting = useTableUIStore((state) => state.fontSize);
  const cellElementHeight = getCellElementHeight(rowHeight);
  const cellFontSize = getCellFontSizeRem(fontSizeSetting);
  // Zustand Trade Data
  const tradeDataFromStore = tradeDataStore((state) => state.tradeData);
  const currentStoreTrade = tradeDataFromStore.find(
    (es) => es.esid === data.esid,
  );

  // --- Trade Settings - from store.
  const allTradeSettings = tradeSettingsStore(
    (state) => state.tradeSettingsData,
  );
  const [thisTradeSettings, setThisTradeSettings] = useState(
    findThisTradeSettings({ esid: data.esid }),
  );

  const isOrder =
    thisTradeSettings?.tradeTypes?.find((type) => type.active)?.name ===
    TradeType.Order;

  useEffect(() => {
    setThisTradeSettings(findThisTradeSettings({ esid: data.esid }));
  }, [allTradeSettings]);
  // --- End Trade Settings.

  const [showRenameTooltip, setShowRenameTooltip] = useState(false);

  function handleTooltipClick() {
    setShowRenameTooltip((prev) => !prev); // Toggle tooltip open/close
  }

  useEffect(() => {
    // NEED TO STOP THE FAVOURITES FROM GETTING LOADED IF THE TRADE IS GETTING EDITED RATHER THEN LOADED AT NEW.
    initialPanelsFromAPI(
      getAccessTokenSilently,
      setCurrentActiveTab,
      setIsDefault,
      currentStoreTrade,
      isOrder,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch for trade type changes - reset dropdown when switching to Order mode with multi-CP panel
  useEffect(() => {
    if (isEditPanel) {
      return; // Skip for edit panel - type is already set on the trade
    }

    // Calculate isOrderMode inside the effect to ensure we have the latest value
    const isOrderMode =
      thisTradeSettings?.tradeTypes?.find((type) => type.active)?.name ===
      TradeType.Order;

    if (isOrderMode) {
      // Check if current active panel has >1 CP
      const activePanel = panelsData.find((p) => p.active);
      if (
        activePanel &&
        activePanel.counterparties &&
        activePanel.counterparties.length > 1
      ) {
        // Reset to default panel
        setPanelItemAsActive(defaultUserPanel);
        setCurrentActiveTab(defaultUserPanel);
        setIsDefault(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thisTradeSettings?.tradeTypes]);

  function saveUpdatedConfig() {
    const currentPanel = panelsData.find((panel) => panel.active);
    if (currentPanel?.id === "-1") {
      generateToastMessageSelectingPanel({
        action: "savePanel",
        result: "error",
      });
      return; // exit early
    }

    if (!thisTradeSettings) {
      return; // Second early return if no counterparties
    }

    const activeCounterPartyConfig: IPanelCounterparty[] = [];
    thisTradeSettings.counterpartiesList.forEach((cp) => {
      if (cp.active) {
        activeCounterPartyConfig.push({
          code: cp.code,
          desks:
            cp.activeDesks &&
            cp.desks &&
            cp.activeDesks.length > 0 &&
            cp.activeDesks.length !== cp.desks.length
              ? cp.activeDesks
              : undefined,
        });
      }
    });

    // look at the changing counterparties and update the store and DB:
    //Set the selected counterparties onto the current panel config
    if (currentPanel) {
      updatePanelCounterparties(
        activeCounterPartyConfig,
        getAccessTokenSilently,
      );

      generateToastMessageSelectingPanel({
        action: "savePanel",
        result: "success",
        additionalData: currentPanel?.label,
      });
    }
  }

  function addingNewPanel() {
    if (panelsData.length < 12) {
      createNewPanel(getAccessTokenSilently); // store function
      generateToastMessageSelectingPanel({
        action: "addPanel",
        result: "success",
      });
      return;
    }

    generateToastMessageSelectingPanel({
      action: "addPanel",
      result: "error",
      additionalData: "Too many existing panels.",
    });
  }

  const saveAndUpdateButtons = [
    {
      name: "rename",
      symbol: editPencil,
      action: handleTooltipClick,
    }, // editCurrentPanelName },
    { name: "savePanel", symbol: saveIcon, action: saveUpdatedConfig },
    // { name: "update", symbol: squarePlus, action: editCurrentPanelName },
  ];

  const handleSelectedPanelChange = (
    option: IPanelSelection,
  ): { success: boolean } => {
    if (!thisTradeSettings) {
      return { success: false };
    }

    // Check if in Order mode - Orders can only have 1 counterparty
    const isOrderMode = isEditPanel
      ? currentStoreTrade?.type === "Order"
      : (thisTradeSettings?.tradeTypes?.find((type) => type.name === "Order")
          ?.active ?? false);

    // Validate panel selection for Order mode
    if (
      isOrderMode &&
      option.counterparties &&
      option.counterparties.length > 1
    ) {
      toast.error("Orders can only be sent to 1 counterparty.");
      return { success: false };
    }

    if (option.counterparties) {
      const newCounterpartyList = thisTradeSettings.counterpartiesList.map((cp) => {
        const savedCp = option.counterparties!.find((s) => s.code === cp.code);
        if (!savedCp) return { ...cp, active: false, activeDesks: [] };
        return {
          ...cp,
          active: true,
          activeDesks: savedCp.desks,
        };
      });
      // UPDATE THE ZUSTAND STORE
      setTradeSettingsSelectedCounterparties({
        esid: data.esid,
        newCounterpartyList,
      });
    } else {
      toast(`Selected panel has no counterparties saved to it.`);
      const emptyCPList = thisTradeSettings.counterpartiesList.map((cp) => ({
        ...cp,
        active: false,
      }));
      setTradeSettingsSelectedCounterparties({
        esid: data.esid,
        newCounterpartyList: emptyCPList,
      });
    }
    return { success: true };
  };

  function handleSingleClick(item: IPanelSelection) {
    const result = handleSelectedPanelChange(item);

    if (!result.success) {
      // Validation failed (e.g., Order mode with multi-CP panel)
      // Reset to default panel
      setPanelItemAsActive(defaultUserPanel);
      setCurrentActiveTab(defaultUserPanel);
      updateIsDefaultFlag(defaultUserPanel);
      return;
    }

    setPanelItemAsActive(item);
    setCurrentActiveTab(item);
    updateIsDefaultFlag(item);
  }

  function handleDeletingItem(item: IPanelSelection) {
    deletPanelItem(item, handleSelectedPanelChange, getAccessTokenSilently);
    // If the active item is getting deleted, need to set the CP list and Active Tab back to defaults.
    if (item.active) {
      setCurrentActiveTab(defaultUserPanel);
      updateIsDefaultFlag(defaultUserPanel);
    }
  }

  // Is default is used to provide a way of toggleState of the save and edit buttons.
  // Idea is to not have the buttons show when the default panel is active.
  const [isDefault, setIsDefault] = useState(true);

  function updateIsDefaultFlag(item: IPanelSelection) {
    if (item.id === "-1") {
      setIsDefault(true);
      return;
    }
    setIsDefault(false);
  }

  return (
    <div className="flex w-fit flex-row items-center text-black">
      {panelsData.length === 0 ? (
        <div></div>
      ) : (
        <FormControl sx={{ minWidth: 120 }}>
          <Select
            labelId="demo-small-select-label cube-select-styles py-2"
            id="demo-select-small cube-select-styles"
            className="dark:bg-tranparent border p-0 dark:border-[#4B5563] dark:text-xs dark:text-gray-400"
            style={{ height: `${cellElementHeight}px` }}
            value={currentActiveTab?.label || ""}
            // onClose={setAllToStopEditing}
            inputProps={{ "aria-label": "Without label" }}
            renderValue={(selected) => {
              // Find the selected item to display only the label
              const selectedItem = panelsData.find(
                (item) => item.label === selected,
              );
              return selectedItem ? (
                <div className="relative right-1 top-[1px]" style={{ fontSize: cellFontSize }}>
                  {selectedItem.label}
                </div>
              ) : (
                ""
              );
            }}
            displayEmpty
            MenuProps={{
              sx: {
                "& .MuiMenu-paper": {
                  border: "1px solid gray !important",
                  paddingTop: "0px !important",
                  paddingBottom: "0px !important",
                  width: "120px",
                  backgroundColor:
                    theme === "dark"
                      ? cubeCustomColors.cubeBaseDark[700]
                      : cubeCustomColors.cubeBase[700],
                },
                "& .MuiMenuItem-root": {
                  paddingLeft: "5px !important",
                  paddingRight: "5px !important",
                  minWidth: "120px !important",
                  height: `${cellElementHeight + 3}px`,
                  fontSize: cellFontSize,
                  backgroundColor:
                    theme === "dark"
                      ? cubeCustomColors.cubeBaseDark[700]
                      : cubeCustomColors.cubeBase[700],
                },
                "& .MuiMenuItem-root.Mui-selected": {
                  backgroundColor:
                    theme === "dark"
                      ? cubeCustomColors.cubeBrand[200] // Dark
                      : cubeCustomColors.cubeBrand[100],
                  color: "black !important",
                },
                "& .MuiMenuItem-root.Mui-selected:hover": {
                  backgroundColor:
                    theme === "dark"
                      ? cubeCustomColors.cubeBrand[200] // Dark hover
                      : cubeCustomColors.cubeBrand[100],
                  color: "black !important",
                },
              },
            }}
          >
            {panelsData.map((item) => (
              <MenuItem
                key={item.id}
                value={item.label}
                onClick={() => handleSingleClick(item)}
                // css-1rju2q6-MuiButtonBase-root-MuiMenuItem-root.Mui-selected (used for the active colour.)
                className="flex items-center justify-between text-gray-500 dark:bg-cubeBaseDark-700 dark:text-xs dark:text-gray-200" // Hover the same as the 'hoverMenu' Button varient.
              >
                <div className="flex w-full items-center justify-between">
                  <div className="flex w-full flex-row items-center overflow-hidden">
                    <div className="pr-1 ">
                      <Button
                        variant="favourite"
                        className={`${item.favourite && "bg-yellow-500"}`}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevents triggering Select's onChange
                          setPanelItemAsfavourite(item, getAccessTokenSilently);
                        }}
                      >
                        {favouriteStar}
                      </Button>
                    </div>
                    <div className="relative top-[0.1rem] flex w-[90%] items-center overflow-hidden ">
                      {item.label}
                    </div>
                  </div>

                  {item.id != "-1" ? (
                    <Button
                      variant="exit"
                      className="!dark:text-white"
                      size="icon_sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletingItem(item);
                      }}
                    >
                      {closeSM}
                    </Button>
                  ) : (
                    <Button
                      variant="add"
                      size="icon_sm"
                      data-tooltip-id={"add"}
                      onClick={(e) => {
                        e.stopPropagation();
                        addingNewPanel();
                      }}
                    >
                      {squarePlusSM}
                    </Button>
                  )}
                </div>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {isDefault ? (
        <div></div>
      ) : (
        <div className="flex flex-row gap-1 px-1">
          {saveAndUpdateButtons.map((btn, index) => {
            return (
              <div key={index} data-tooltip-id={btn.name}>
                <Button
                  variant="icon"
                  id={btn.name}
                  key={index}
                  onClick={btn.action}
                  className="h-4 w-4 border border-gray-700 "
                >
                  {btn.symbol}
                </Button>
              </div>
            );
          })}
        </div>
      )}
      <Tooltip
        id={"rename"}
        place="right-start"
        clickable={true}
        openOnClick={true}
        isOpen={showRenameTooltip} // Controls tooltip visibility
        setIsOpen={setShowRenameTooltip} // Allows tooltip to manage its state
        globalCloseEvents={{ clickOutsideAnchor: true, escape: true }} // Enables closing on outside click and ESC        className="z-40"
        opacity={1}
        // opacity={0.9}
        border="1px solid gray"
        style={{
          zIndex: 10,
          backgroundColor:
            theme === "dark"
              ? cubeCustomColors.cubeBaseDark[700]
              : cubeCustomColors.cubeBase[700],
        }}
      >
        <div className="flex w-fit flex-col text-black dark:text-gray-200">
          <div className="flex ">
            <EditableTextRenameCP
              currentActiveTab={currentActiveTab}
              panelsData={panelsData}
              setCurrentActiveTab={setCurrentActiveTab}
              setShowRenameTooltip={setShowRenameTooltip}
            />
          </div>
        </div>
      </Tooltip>
    </div>
  );
};

export default ReactSelectSavedPanel;
